import router from '@/router/';
import { builder, valdationRequiredFields } from '@/models/';
import getEndpoint from '@/configs/endpoints';
import { get, post, deleteRequest } from '@/helpers/requests';
import configs from '@/server/server';

/**
 * [action]
 * Registo do campo em store.
 * Só poderá registar o campo em store quando passa nas validações.
 *
 * @param {String} field.name - nome do campo
 * @param {String} field.value - valor do campo
 * @since 1.0.0
 * @category action
 * @author Tiago Martinho
 *
 */
const registerField = ({ commit }, field ) => {
    commit( 'REGISTER_FIELD', field );
};

/**
 * [action]
 * Eliminação do campo em store.
 * Caso o campo não passe nas validações, é necessário apaga-lo da store.
 *
 * @param {String} field.name - nome do campo
 * @since 1.0.0
 * @category action
 * @author Tiago Martinho
 *
 */
const deleteField = ({ commit }, field ) => {
    commit( 'DELETE_FIELD', field );
};

/**
 * [action]
 * Conforme o tipo de acção ( active / block ) irá
 * controlar a barra de acção.
 *
 * @param {Object} config
 * @param {String} config.type
 * @param {Boolean} config.value
 * @since 1.0.0
 * @category action
 * @author Tiago Martinho
 *
 */
const controlActionBar = ({ commit }, { type = 'block', value }) => {
    commit( 'CONTROL_ACTION_BAR', { type, value });
};

/**
 * [action]
 * Reset dos erros que podem estar associados
 * ao campo.
 *
 * @param {Object} payload
 * @param {String} payload.type - 'missing' || 'api'
 * @param {String} payload.fieldName
 * @since 1.0.0
 * @category action
 * @author Tiago Martinho
 *
 */
const resetError = ({ commit }, payload ) => {
    commit( 'RESET_ERROR', payload );
};

/**
 * [action]
 * Reset do payload para o valor default.
 *
 * @since 1.0.0
 * @category action
 * @author Tiago Martinho
 *
 */
const resetPayload = ({ commit }) => {
    commit( 'RESET_PAYLOAD' );
};

/**
 * [action]
 * Accionado o trigger para fazer reset
 * ao payload, erros & helpers em simultaneo.
 *
 * @since 1.0.0
 * @category action
 * @author Tiago Martinho
 *
 */
const resetState = ({ commit }, resetHelper = true ) => {
    commit( 'RESET_ALL_ERRORS' );
    commit( 'RESET_PAYLOAD' );

    if ( resetHelper )
        commit( 'RESET_HELPERS' );
};

/**
 * [action]
 * Accionado o trigger para fazer reset
 * ao valor das inputs.
 *
 * @since 1.0.0
 * @category action
 * @author Tiago Martinho
 *
 */
const resetModel = ({ commit, dispatch }) => {
    commit( 'RESET_MODEL' );
    dispatch( 'resetState', false );
};

/**
 * [action]
 * Adicionado um helper de forma a
 * ser possível validar se alguma data é
 * valida para ser enviada para API.
 *
 * @param {Object} helper
 * @since 1.0.0
 * @category action
 * @author Tiago Martinho
 *
 */
const saveHelper = ({ commit }, helper ) => {
    commit( 'SAVE_HELPER', helper );
};

/**
 * [action]
 * Toggle do trigger
 *
 * @param {Boolean} trigger
 * @since 1.0.0
 * @category action
 * @author Tiago Martinho
 *
 */
const toggleTrigger = ({ commit }, trigger = true ) => {
    commit( 'TOGGLE_TRIGGER', trigger );
};

/**
 * [action]
 * Gere a resposta proveniente de outros
 * pontos que não submissões para API.
 *
 * @param {Object} response
 * @since 1.0.0
 * @category action
 * @author Tiago Martinho
 *
 */
const manageResponse = ({ commit }, response ) => {
    commit( 'MANAGE_RESPONSE', response );
};

/**
 * [action]
 * Submissão do modelo para a API. ( campanha, utilizador, etc )
 * Conforme o modelo, é feito a validação se todos os campos obrigatórios foram preenchidos.
 * Se passar nesta validação, é gerado o JSON do modelo e obtido o endpoint de
 * forma a ser submitido para API. No final do pedido, é guardado na store a resposta, de
 * forma a ser possível apresentar o estado da acção ao utilizador.
 *
 * @param {String} model
 * @param {Boolean} isEdit
 * @param {String} apiModel
 * @param {String} modelId
 * @param {String} successAction
 * @since 1.0.0
 * @category action
 * @author Tiago Martinho
 *
 */
const submit = async ({ state, commit }, { typeOfPreparation = 'model' }) => {
    const { prepare, successAction } = configs[typeOfPreparation]();
    const settings = prepare( state.payload );

    // se já existir loading, se não existir model ou
    // se existir resposta, não executa a submissão.
    if ( state.loading || state.response.has || !settings.model )
        return;

    const missingFields = await valdationRequiredFields( settings.model, state.payload, settings.isEdit );

    // validação se existe campos em falta.
    if ( Object.keys( missingFields ).length ) {
        commit( 'MANAGE_MISSING_FIELDS', missingFields );
        return;
    }

    commit( 'TOGGLE_LOADING', true );

    // obtém o object dos campos preenchidos
    const fields = await builder({ model: settings.model, fields: state.payload, helpers: settings.helpers || state.helpers });

    const formData = new FormData();

    // passa para formData - por causa dos ficheiros.
    Object.keys( fields ).forEach( key => {
        formData.append( key, fields[key] ); // transforma o objecto em formData
    });

    // se for uma edição e não exista já o
    // method no objecto.
    if ( settings.isEdit && !state.payload.method ) {
        formData.append( '_method', 'put' );
        formData.append( 'id', settings.modelId );
    }

    const endpoint = settings.customEndpoint || getEndpoint[settings.apiModel].crud;
    const response = await post( endpoint, formData )
        .then( ({ data }) => ({
            has: true,
            type: 'success',
            message: data.message,
            content: data.content.data
        }) )
        .catch( error => ({
            has: true,
            type: 'insuccess',
            message: error.message,
            errors: error.content.errors
        }) );
    commit( 'MANAGE_RESPONSE', response );
    commit( 'TOGGLE_LOADING', false );

    // caso seja sucesso:
    // -- se for uma criação, o utilizador é redirecionado para
    //    a página da nova entrada.
    // -- remoção do payload.
    // -- remoção dos erros.
    if ( response.type === 'success' ) {
        successAction({
            commit,
            payload: {
                response,
                newModel: state.payload,
                ...( settings.customEndpoint && { customRoute: settings.customEndpoint })
            }
        });

        commit( 'RESET_ALL_ERRORS' );
        commit( 'RESET_PAYLOAD' );
    }
};

/**
 * [action]
 * Eliminação de um modelo ou de algo relativo a um modelo. ( utilizador, token, etc )
 *
 * @param {Object} state
 * @param {Function} commit
 * @param {Function} dispatch
 * @param {String} typeOfPreparation
 * @since 1.0.0
 * @category action
 * @author Tiago Martinho
 *
 */
const processDelete = async ({ state, commit, dispatch }, { typeOfPreparation = null }) => {
    // activação do loading
    commit( 'TOGGLE_LOADING', true );
    const route = router.currentRoute;
    const id = state.payload.id || route.params.id;
    const response = await deleteRequest( state.payload.endpoint, { data: { id } })
        .then( ({ data }) => ({
            has: true,
            type: 'success',
            message: data.message,
            content: data.content.data
        }) )
        .catch( error => ({
            has: true,
            type: 'insuccess',
            message: error.message,
            errors: error.content.errors
        }) );
    // procedimento no final da submissão
    dispatch( 'finalSubmission', response );

    // se for necessário fazer mais alguma coisa, para além
    // do comportamento default.
    if ( typeOfPreparation ) {
        const { successAction } = configs[typeOfPreparation]();
        successAction({ commit, payload: { response, newModel: { id, isDelete: true } } });
    }
};

/**
 * [action]
 * Exportação de um ficheiro.
 *
 * @param {Object} state
 * @param {Function} commit
 * @param {Function} dispatch
 * @param {String} typeOfPreparation
 * @since 1.0.0
 * @category action
 * @author Jorge Monteiro
 *
 */
const processExport = async ({ state, commit, dispatch }) => {
    // activação do loading
    commit( 'TOGGLE_LOADING', true );

    const { date, reportingRange, campaignName } = state.payload;
    let toDateValue = '';
    let fromDateValue = '';

    if ( reportingRange ) {
        toDateValue = reportingRange.toDate;
        fromDateValue = reportingRange.fromDate;
    }

    const response = await get( state.payload.endpoint, {
        date,
        ...( toDateValue && { toDate: toDateValue }),
        ...( fromDateValue && { fromDate: fromDateValue }),
        campaignName
    })
        .then( ({ data }) => ({
            has: true,
            type: 'success',
            message: data.message,
            content: data.content.data
        }) )
        .catch( error => ({
            has: true,
            type: 'insuccess',
            message: error.message,
            errors: error.content.errors
        }) );

    // procedimento no final da submissão
    dispatch( 'finalSubmission', response );
};

/**
 * [action]
 * No final da submissão provenientes das modais, é
 * necessário chamar as seguintes mutações.
 *
 * @param {Function} commit
 * @param {Object} response
 * @since 1.0.0
 * @category action
 * @author Tiago Martinho
 *
 */
const finalSubmission = ({ commit }, response ) => {
    commit( 'TOGGLE_TRIGGER', true );
    commit( 'MANAGE_RESPONSE', response );
    commit( 'TOGGLE_LOADING', false );
    commit( 'RESET_ALL_ERRORS' );
    commit( 'RESET_PAYLOAD' );
    commit( 'CONTROL_ACTION_BAR', { type: 'block', value: false });
};

/**
 * [action]
 * Toggle do loading
 *
 * @param {Boolean} isLoading
 * @since 1.2.3
 * @category action
 * @author Jorge Monteiro
 *
 */
const toggleLoading = ({ commit }, isLoading = true ) => {
    commit( 'TOGGLE_LOADING', isLoading );
};

/**
 * [action]
 * Gravar os dados para pesquisa
 *
 * @param {Object} searchData
 * @since 1.0.0
 * @category action
 *
*/
const saveSearchData = ({ commit }, searchData ) => {
    commit( 'SAVE_SEARCH_DATA', searchData );
};

/**
 * [action]
 * Gravar os dados para pesquisa
 *
 * @param {Object} searchData
 * @since 1.2.5
 * @category action
 *
*/
const resetSearchData = ({ commit }) => {
    commit( 'RESET_SEARCH_DATA' );
};

export default {
    registerField,
    deleteField,
    controlActionBar,
    resetError,
    resetPayload,
    resetState,
    resetModel,
    toggleTrigger,
    toggleLoading,
    saveHelper,
    manageResponse,
    submit,
    processDelete,
    processExport,
    finalSubmission,
    saveSearchData,
    resetSearchData
};